import React, { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import styled from "styled-components";
import { motion } from "framer-motion";
import {
  FiUser,
  FiMail,
  FiPhone,
  FiMapPin,
  FiClock,
  FiUpload,
  FiSave,
  FiX,
  FiEdit,
  FiCamera,
} from "react-icons/fi";
import { vendorsAPI, uploadAPI } from "../../services/api";
import toast from "react-hot-toast";

const SettingsContainer = styled.div`
  padding: 2rem;
  max-width: 800px;
  margin: 0 auto;
`;

const PageDescription = styled.div`
  margin-bottom: 2rem;
  text-align: center;

  p {
    color: #666;
    font-size: 1.125rem;
  }
`;

const SettingsCard = styled(motion.div)`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #2d5016;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  &.full-width {
    grid-column: 1 / -1;
  }
`;

const Label = styled.label`
  font-weight: 500;
  color: #333;
  font-size: 0.875rem;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 2px solid #e0e0e0;
  border-radius: 6px;
  font-size: 1rem;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #2d5016;
  }

  &.error {
    border-color: #dc3545;
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 2px solid #e0e0e0;
  border-radius: 6px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #2d5016;
  }

  &.error {
    border-color: #dc3545;
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 2px solid #e0e0e0;
  border-radius: 6px;
  font-size: 1rem;
  background: white;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #2d5016;
  }

  &.error {
    border-color: #dc3545;
  }
`;

const ErrorMessage = styled.span`
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 0.25rem;
`;

const ProfileImageSection = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const ProfileImageContainer = styled.div`
  position: relative;
  width: 150px;
  height: 150px;
  border-radius: 12px;
  overflow: hidden;
  background: linear-gradient(135deg, #2d5016, #4a7c59);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 3rem;
  cursor: pointer;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
  }
`;

const ImageUploadOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  cursor: pointer;

  &:hover {
    opacity: 1;
  }

  .upload-icon {
    color: white;
    font-size: 1.5rem;
  }
`;

const ImageUploadInput = styled.input`
  display: none;
`;

const ImageInfo = styled.div`
  flex: 1;

  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #2d5016;
    margin-bottom: 0.5rem;
  }

  p {
    color: #666;
    margin-bottom: 1rem;
  }
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1rem;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled(Button)`
  background: #2d5016;
  color: white;

  &:hover:not(:disabled) {
    background: #1a3a0e;
  }
`;

const SecondaryButton = styled(Button)`
  background: #6c757d;
  color: white;

  &:hover:not(:disabled) {
    background: #5a6268;
  }
`;

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  font-size: 1.125rem;
  color: #666;
`;

const ErrorState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #dc3545;

  h3 {
    margin-bottom: 1rem;
  }
`;

// Validation schema
const vendorSchema = yup.object({
  businessName: yup.string().required("Business name is required"),
  businessType: yup.string().required("Business type is required"),
  cuisineType: yup.string().required("Cuisine type is required"),
  businessAddress: yup.string().required("Business address is required"),
  businessPhone: yup.string().required("Business phone is required"),
  businessEmail: yup
    .string()
    .email("Invalid email")
    .required("Business email is required"),
  deliveryRadius: yup
    .number()
    .min(1, "Delivery radius must be at least 1km")
    .required("Delivery radius is required"),
  description: yup.string(),
});

const VendorSettings = () => {
  const [isUploading, setIsUploading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const fileInputRef = useRef(null);
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm({
    resolver: yupResolver(vendorSchema),
    defaultValues: {
      businessName: "",
      businessType: "",
      cuisineType: "",
      businessAddress: "",
      businessPhone: "",
      businessEmail: "",
      deliveryRadius: 5,
      description: "",
    },
  });

  // Fetch vendor data
  const {
    data: vendorData,
    isLoading,
    error,
  } = useQuery("vendor-settings", () => vendorsAPI.getMyVendor());

  // Populate form when vendor data is loaded
  useEffect(() => {
    // React Query returns response.data, and backend also has data property
    // So we need vendorData.data.data.vendor (same pattern as VendorDashboard)
    const vendor = vendorData?.data?.data?.vendor || vendorData?.data?.vendor;
    
    if (vendor) {
      console.log("Setting form with vendor data:", vendor);
      
      // Reset form with vendor data
      reset({
        businessName: vendor.business_name || "",
        businessType: vendor.business_type || "",
        cuisineType: vendor.cuisine_type || "",
        businessAddress: vendor.business_address || "",
        businessPhone: vendor.business_phone || "",
        businessEmail: vendor.business_email || "",
        deliveryRadius: vendor.delivery_radius || 5,
        description: vendor.description || "",
      });
      
      // Set preview image if available
      if (vendor.profile_image_url) {
        setPreviewImage(vendor.profile_image_url);
      }
    } else {
      console.log("Vendor data structure:", vendorData);
    }
  }, [vendorData, reset]);

  // Update vendor mutation
  const updateVendorMutation = useMutation(
    (vendorData) => vendorsAPI.updateMyVendor(vendorData),
    {
      onSuccess: () => {
        toast.success("Profile updated successfully!");
        queryClient.invalidateQueries("vendor-settings");
        queryClient.invalidateQueries("vendor-dashboard");
      },
      onError: (error) => {
        toast.error(
          error.response?.data?.message || "Failed to update profile"
        );
      },
    }
  );

  // Handle image upload
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must be less than 5MB");
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append("image", file);

      const response = await uploadAPI.uploadImage(formData);
      const imageUrl = response.data.data.url;

      // Update the vendor profile with the new image URL
      await updateVendorMutation.mutateAsync({
        profileImageUrl: imageUrl,
      });

      setPreviewImage(imageUrl);
      toast.success("Profile image updated successfully!");
    } catch (error) {
      console.error("Image upload error:", error);
      toast.error("Failed to upload image");
    } finally {
      setIsUploading(false);
    }
  };

  // Handle form submission
  const onSubmit = async (data) => {
    try {
      await updateVendorMutation.mutateAsync(data);
    } catch (error) {
      console.error("Update error:", error);
    }
  };

  const vendor = vendorData?.data?.data?.vendor || vendorData?.data?.vendor;

  if (isLoading) {
    return (
      <SettingsContainer>
        <LoadingState>
          <div className="loading-spinner" />
          Loading settings...
        </LoadingState>
      </SettingsContainer>
    );
  }

  if (error) {
    return (
      <SettingsContainer>
        <ErrorState>
          <h3>Error loading settings</h3>
          <p>{error.message}</p>
        </ErrorState>
      </SettingsContainer>
    );
  }

  return (
    <SettingsContainer>
      <PageDescription>
        <p>Manage your business information and profile</p>
      </PageDescription>

      <SettingsCard
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <SectionTitle>
          <FiUser />
          Profile Image
        </SectionTitle>

        <ProfileImageSection>
          <ProfileImageContainer onClick={() => fileInputRef.current?.click()}>
            {previewImage ? (
              <img src={previewImage} alt="Profile" />
            ) : (
              <div className="placeholder">
                <span>🍽️</span>
                <span>{vendor?.business_name?.charAt(0) || "V"}</span>
              </div>
            )}
            <ImageUploadOverlay>
              <FiCamera className="upload-icon" />
            </ImageUploadOverlay>
            <ImageUploadInput
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              disabled={isUploading}
            />
          </ProfileImageContainer>

          <ImageInfo>
            <h3>Profile Image</h3>
            <p>
              Upload a professional photo of your business or logo. This will be
              displayed on your vendor card and profile page.
            </p>
            <p style={{ fontSize: "0.875rem", color: "#666" }}>
              Recommended size: 400x400px or larger. Max file size: 5MB.
            </p>
          </ImageInfo>
        </ProfileImageSection>
      </SettingsCard>

      <SettingsCard
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <SectionTitle>
          <FiEdit />
          Business Information
        </SectionTitle>

        <form onSubmit={handleSubmit(onSubmit)}>
          <FormGrid>
            <FormGroup className="full-width">
              <Label htmlFor="businessName">Business Name *</Label>
              <Input
                id="businessName"
                type="text"
                {...register("businessName")}
                className={errors.businessName ? "error" : ""}
              />
              {errors.businessName && (
                <ErrorMessage>{errors.businessName.message}</ErrorMessage>
              )}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="businessType">Business Type *</Label>
              <Select
                id="businessType"
                {...register("businessType")}
                className={errors.businessType ? "error" : ""}
              >
                <option value="">Select business type</option>
                <option value="restaurant">Restaurant</option>
                <option value="food truck">Food Truck</option>
                <option value="catering">Catering</option>
                <option value="home kitchen">Home Kitchen</option>
                <option value="bakery">Bakery</option>
                <option value="other">Other</option>
              </Select>
              {errors.businessType && (
                <ErrorMessage>{errors.businessType.message}</ErrorMessage>
              )}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="cuisineType">Cuisine Type *</Label>
              <Select
                id="cuisineType"
                {...register("cuisineType")}
                className={errors.cuisineType ? "error" : ""}
              >
                <option value="">Select cuisine type</option>
                <option value="Guyanese">Guyanese</option>
                <option value="Indian">Indian</option>
                <option value="Chinese">Chinese</option>
                <option value="Caribbean">Caribbean</option>
                <option value="Creole">Creole</option>
                <option value="International">International</option>
                <option value="Mixed">Mixed</option>
              </Select>
              {errors.cuisineType && (
                <ErrorMessage>{errors.cuisineType.message}</ErrorMessage>
              )}
            </FormGroup>

            <FormGroup className="full-width">
              <Label htmlFor="description">Business Description</Label>
              <TextArea
                id="description"
                {...register("description")}
                placeholder="Tell customers about your business, specialties, and what makes you unique..."
                className={errors.description ? "error" : ""}
              />
              {errors.description && (
                <ErrorMessage>{errors.description.message}</ErrorMessage>
              )}
            </FormGroup>

            <FormGroup className="full-width">
              <Label htmlFor="businessAddress">Business Address *</Label>
              <Input
                id="businessAddress"
                type="text"
                {...register("businessAddress")}
                placeholder="Enter your business address"
                className={errors.businessAddress ? "error" : ""}
              />
              {errors.businessAddress && (
                <ErrorMessage>{errors.businessAddress.message}</ErrorMessage>
              )}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="businessPhone">Business Phone *</Label>
              <Input
                id="businessPhone"
                type="tel"
                {...register("businessPhone")}
                placeholder="e.g., +592 123 4567"
                className={errors.businessPhone ? "error" : ""}
              />
              {errors.businessPhone && (
                <ErrorMessage>{errors.businessPhone.message}</ErrorMessage>
              )}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="businessEmail">Business Email *</Label>
              <Input
                id="businessEmail"
                type="email"
                {...register("businessEmail")}
                placeholder="business@example.com"
                className={errors.businessEmail ? "error" : ""}
              />
              {errors.businessEmail && (
                <ErrorMessage>{errors.businessEmail.message}</ErrorMessage>
              )}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="deliveryRadius">Delivery Radius (km) *</Label>
              <Input
                id="deliveryRadius"
                type="number"
                min="1"
                max="50"
                {...register("deliveryRadius")}
                className={errors.deliveryRadius ? "error" : ""}
              />
              {errors.deliveryRadius && (
                <ErrorMessage>{errors.deliveryRadius.message}</ErrorMessage>
              )}
            </FormGroup>
          </FormGrid>

          <div style={{ display: "flex", gap: "1rem", marginTop: "2rem" }}>
            <PrimaryButton
              type="submit"
              disabled={updateVendorMutation.isLoading}
            >
              <FiSave />
              {updateVendorMutation.isLoading ? "Saving..." : "Save Changes"}
            </PrimaryButton>
            <SecondaryButton
              type="button"
              onClick={() => reset()}
              disabled={updateVendorMutation.isLoading}
            >
              <FiX />
              Reset
            </SecondaryButton>
          </div>
        </form>
      </SettingsCard>
    </SettingsContainer>
  );
};

export default VendorSettings;
